/**
 * Scans a chat turn's `tool_calls` array for the most recent snapshot
 * the agent produced.
 *
 * Backend tool contract:
 *
 *   * `draft_circuit` (tools/mutate_circuit.py) returns `{ circuit_id,
 *     snapshot_id, parent_snapshot_id: null, ... }` — the root
 *     snapshot of a freshly-drafted scratch circuit.
 *   * `mutate_circuit` returns `{ circuit_id, snapshot_id,
 *     parent_snapshot_id, ... }` (chat tool surface) or `{ snapshot_id,
 *     parent_snapshot_id, ir, handle_to_component_id, handle_to_net_id }`
 *     (REST API surface — same field names, more fields).
 *   * `merge_bom` returns `{ snapshot_id, parent_snapshot_id, ir,
 *     matched, ... }`.
 *   * `render_circuit` returns `{ summary, snapshot_id?, version_id?,
 *     scope_to?, title?, caption?, ... }` — `snapshot_id` is set when
 *     the render targets a working buffer, `version_id` when a
 *     committed version. Either way the chat-side IterationCard can
 *     embed the canvas inline from the same source.
 *   * `commit_design` returns `{ version_id, version_number, ... }`
 *     — no snapshot_id (the snapshot was promoted to a version, so we
 *     don't surface a draft pill).
 *
 * The hook returns the LAST snapshot-producing call's metadata. If
 * multiple `mutate_circuit` calls fire in one turn (common with
 * multi-step reasoning), the user sees the final snapshot — that's the
 * one downstream tools (validate, commit) would target. `render_circuit`
 * has the lowest priority — if a turn drafted/mutated AND rendered, the
 * draft/mutate snapshot wins because that's the actionable one. The
 * render call's snapshot_id is the same value anyway.
 *
 * Pure / cheap — no network, no React Query subscription. The card
 * itself fetches snapshot detail via `useSnapshot(snapshot_id)`.
 */
import type { ToolCall } from "@models/chat";
import type { CircuitOp } from "@models/circuit-ir";
import { useMemo } from "react";

/** Tool names whose result payload carries a snapshot the chat should
 *  surface as an iteration card. Order matters for tie-breaking — the
 *  hook returns the LAST matching call, so when a turn does
 *  `draft_circuit` then `render_circuit` both contribute the same
 *  snapshot_id and the result is identical. When a turn does
 *  `mutate_circuit` then `render_circuit`, the mutate call's
 *  snapshot_id is the latest and the render's matches it. */
const SNAPSHOT_PRODUCING_TOOLS = new Set([
  "draft_circuit",
  "mutate_circuit",
  "merge_bom",
  "render_circuit",
]);

/** Tool name that promotes a snapshot to a real version. */
const COMMIT_TOOL = "commit_design";

export interface DetectedSnapshot {
  /** The snapshot id the agent produced — drives the IterationCard. */
  snapshotId: string;
  /** The previous snapshot id, when the tool returns it (mutate /
   *  merge_bom both do). Null when not available — the IterationCard
   *  uses this to enable the "Diff vs prior" gesture. */
  parentSnapshotId: string | null;
  /** The typed ops the agent applied, when the tool was `mutate_circuit`.
   *  Empty for merge_bom (those args don't carry typed ops). */
  ops: CircuitOp[];
  /** The tool name that produced the snapshot — drives the card's
   *  framing copy ("Edited circuit" vs "Merged BOM"). */
  toolName: string;
  /** When set, this turn ALSO committed the snapshot — the card
   *  switches to a celebratory "Committed as v3.3" frame instead of
   *  the iterative one. */
  committedVersionId: string | null;
}

interface ToolCallResult {
  snapshot_id?: string;
  parent_snapshot_id?: string;
  version_id?: string;
}

function isToolCallResult(value: unknown): value is ToolCallResult {
  return typeof value === "object" && value !== null;
}

export function useDetectSnapshotInTurn(
  toolCalls: ToolCall[] | null | undefined,
): DetectedSnapshot | null {
  return useMemo(() => {
    if (!toolCalls || toolCalls.length === 0) return null;

    let latest: DetectedSnapshot | null = null;
    let committedVersionId: string | null = null;

    for (const call of toolCalls) {
      const result = isToolCallResult(call.result) ? call.result : null;

      if (call.name === COMMIT_TOOL && result?.version_id) {
        committedVersionId = result.version_id;
        continue;
      }

      if (!SNAPSHOT_PRODUCING_TOOLS.has(call.name)) continue;
      if (!result?.snapshot_id) continue;

      // When `render_circuit` fires in the same turn as a draft / mutate
      // / merge_bom call, both calls' results carry the SAME snapshot_id
      // (render reads the just-written snapshot). The action call's
      // toolName produces a more meaningful header verb ("Drafted
      // circuit" / "Edited circuit") than "Rendered circuit", so we skip
      // the render entry when an earlier call already pinned the same
      // snapshot. A render-only turn (no preceding draft/mutate) still
      // surfaces the card — `latest` is null at this point so the render
      // call goes through.
      const latestSnapshotId: string | null = latest ? latest.snapshotId : null;
      if (
        call.name === "render_circuit" &&
        latestSnapshotId === result.snapshot_id
      ) {
        continue;
      }

      // Pull typed ops from the call args when present (mutate_circuit
      // and draft_circuit both carry ops; the others don't).
      let ops: CircuitOp[] = [];
      if (
        (call.name === "mutate_circuit" || call.name === "draft_circuit") &&
        call.args &&
        Array.isArray(call.args.ops)
      ) {
        ops = call.args.ops as CircuitOp[];
      }

      latest = {
        snapshotId: result.snapshot_id,
        parentSnapshotId: result.parent_snapshot_id ?? null,
        ops,
        toolName: call.name,
        committedVersionId: null,
      };
    }

    if (latest && committedVersionId) {
      latest.committedVersionId = committedVersionId;
    }
    return latest;
  }, [toolCalls]);
}
